import React, { useEffect, useState } from 'react';
import { Plus, Trash2, Save, CalendarDays, Loader2 } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const DAYS = ['Lundi', 'Mardi', 'Mercredi', 'Jeudi', 'Vendredi', 'Samedi', 'Dimanche'];

export default function DoctorScheduleManager() {
  const [schedule, setSchedule] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);

  useEffect(() => { load(); }, []);

  const authHeaders = () => ({
    'Authorization': `Bearer ${localStorage.getItem('doctor_token')}`,
    'Content-Type': 'application/json',
  });

  const load = async () => {
    setLoading(true);
    try {
      const r = await fetch(`${API_URL}/api/doctor/my-schedule`, { headers: authHeaders() });
      if (r.ok) setSchedule(await r.json());
    } catch (e) {
      setMessage({ type: 'error', text: 'Impossible de charger le planning.' });
    }
    setLoading(false);
  };

  const setDayEnabled = (day, enabled) => {
    setSchedule(s => ({
      ...s,
      weekly_hours: s.weekly_hours.map(d => d.day === day ? { ...d, enabled } : d),
    }));
  };
  const setRange = (day, idx, field, value) => {
    setSchedule(s => ({
      ...s,
      weekly_hours: s.weekly_hours.map(d => d.day === day
        ? { ...d, ranges: d.ranges.map((r, i) => i === idx ? { ...r, [field]: value } : r) }
        : d),
    }));
  };
  const addRange = (day) => {
    setSchedule(s => ({
      ...s,
      weekly_hours: s.weekly_hours.map(d => d.day === day
        ? { ...d, ranges: [...d.ranges, { start: '14:00', end: '17:00' }] }
        : d),
    }));
  };
  const removeRange = (day, idx) => {
    setSchedule(s => ({
      ...s,
      weekly_hours: s.weekly_hours.map(d => d.day === day
        ? { ...d, ranges: d.ranges.filter((_, i) => i !== idx) }
        : d),
    }));
  };

  const save = async () => {
    setSaving(true);
    setMessage(null);
    try {
      const r = await fetch(`${API_URL}/api/doctor/my-schedule`, {
        method: 'PUT',
        headers: authHeaders(),
        body: JSON.stringify({
          weekly_hours: schedule.weekly_hours,
          slot_duration: schedule.slot_duration || 20,
        }),
      });
      const data = await r.json();
      if (r.ok) {
        setSchedule(data);
        setMessage({ type: 'success', text: 'Planning enregistré ✓' });
      } else {
        setMessage({ type: 'error', text: data.detail || 'Erreur lors de l\'enregistrement.' });
      }
    } catch (e) {
      setMessage({ type: 'error', text: 'Erreur réseau.' });
    }
    setSaving(false);
    setTimeout(() => setMessage(null), 4000);
  };

  if (loading) {
    return (
      <div style={{ padding: '3rem', textAlign: 'center', color: '#64748b' }}>
        <Loader2 size={28} className="spin" /> Chargement du planning…
      </div>
    );
  }
  if (!schedule) return null;

  return (
    <div className="doctor-schedule-manager" data-testid="doctor-schedule-manager">
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: '1rem' }}>
        <CalendarDays size={28} color="#1565C0" />
        <div>
          <h1 style={{ margin: 0, fontSize: '1.4rem' }}>Mes créneaux horaires</h1>
          <p style={{ margin: '4px 0 0', color: '#64748b', fontSize: '0.9rem' }}>
            Définissez les jours et heures où vous êtes disponible pour consulter.
          </p>
        </div>
      </div>

      <div style={{
        display: 'flex', gap: 12, alignItems: 'center', marginBottom: '1.2rem',
        padding: '12px 14px', background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 10,
      }}>
        <label style={{ fontWeight: 600, fontSize: '0.9rem' }}>Durée d&apos;un créneau :</label>
        <select
          value={schedule.slot_duration || 20}
          onChange={e => setSchedule(s => ({ ...s, slot_duration: parseInt(e.target.value) }))}
          data-testid="slot-duration-select"
          style={{ padding: '6px 10px', borderRadius: 6, border: '1px solid #cbd5e1', fontSize: '0.9rem' }}
        >
          {[10, 15, 20, 30, 45, 60].map(m => <option key={m} value={m}>{m} minutes</option>)}
        </select>
      </div>

      <div className="schedule-days">
        {DAYS.map((label, dayIdx) => {
          const d = schedule.weekly_hours.find(x => x.day === dayIdx) || { day: dayIdx, enabled: false, ranges: [] };
          return (
            <div key={dayIdx} className="schedule-day-row" data-testid={`schedule-day-${dayIdx}`}>
              <div className="schedule-day-header">
                <label className="schedule-day-toggle">
                  <input
                    type="checkbox"
                    checked={d.enabled}
                    onChange={e => setDayEnabled(dayIdx, e.target.checked)}
                    data-testid={`day-enabled-${dayIdx}`}
                  />
                  <strong>{label}</strong>
                </label>
                {d.enabled && (
                  <button type="button" className="schedule-add-btn" onClick={() => addRange(dayIdx)} data-testid={`add-range-${dayIdx}`}>
                    <Plus size={14} /> Ajouter une plage
                  </button>
                )}
              </div>
              {d.enabled && (
                <div className="schedule-ranges">
                  {d.ranges.length === 0 && (
                    <p style={{ fontStyle: 'italic', color: '#94a3b8', fontSize: '0.85rem', margin: 0 }}>
                      Aucune plage — ajoutez au moins une plage horaire.
                    </p>
                  )}
                  {d.ranges.map((r, i) => (
                    <div key={i} className="schedule-range" data-testid={`range-${dayIdx}-${i}`}>
                      <input
                        type="time"
                        value={r.start}
                        onChange={e => setRange(dayIdx, i, 'start', e.target.value)}
                        data-testid={`range-start-${dayIdx}-${i}`}
                      />
                      <span>→</span>
                      <input
                        type="time"
                        value={r.end}
                        onChange={e => setRange(dayIdx, i, 'end', e.target.value)}
                        data-testid={`range-end-${dayIdx}-${i}`}
                      />
                      <button type="button" className="schedule-remove-btn" onClick={() => removeRange(dayIdx, i)} data-testid={`range-remove-${dayIdx}-${i}`}>
                        <Trash2 size={14} />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>

      <div className="schedule-footer">
        {message && (
          <div className={`schedule-message ${message.type}`} data-testid="schedule-message">
            {message.text}
          </div>
        )}
        <button
          className="btn-primary schedule-save-btn"
          onClick={save}
          disabled={saving}
          data-testid="schedule-save-btn"
        >
          {saving ? <Loader2 size={16} className="spin" /> : <Save size={16} />}
          {saving ? 'Enregistrement…' : 'Enregistrer mon planning'}
        </button>
      </div>
    </div>
  );
}
